import { useCallback, useEffect, useRef, useState } from "react";
import useListState from "@Util/hook/useListState";
import useQuerySync from "@Util/hook/useQuerySync";
import useOverviewFetch from "@Util/hook/useOverviewFetch";
import { isEmpty, isEqual, normalizeBySchema } from "@Util/utils";
import useEffectOnceWhen from "@Util/hook/useEffectOnceWhen";

/**
 * 自訂 Hook：通用型資料總覽處理工具
 *
 * ✅ 功能包含：
 * - 自動打 API 取得資料（支援首載與瀑布流）
 * - 管理列表狀態（list + pagination + extend）
 * - 支援查詢參數同步到網址（可選）
 * - 可更新單筆資料（例如修改其中一筆項目）
 *
 * ⚠️ 備註：
 * - 如果開啟 enableQueryString，查詢參數會同步到網址列
 * - 預設會根據 defaultSearchParams 判斷是否首載
 *
 * @param {object} props - 傳入參數
 * @param {(params:object)=>Promise<any>} props.getOverviewApi - API 函式（回傳需含 list 與分頁資訊）
 * @param {object} [props.defaultSearchParams] - 預設查詢條件
 * @param {object} [props.onScrollerTo] - 初始滾動到該位置
 * @param {boolean} [props.enableInfiniteScroll] - 是否為瀑布流（append 模式）
 * @param {boolean} [props.isFetchOnMount] - 是否在首次進入畫面自動 fetch
 * @param {boolean} [props.enableQueryString] - 是否啟用 URL Query 同步
 */
const useOverview = ({ getOverviewApi, defaultSearchParams = {}, enableInfiniteScroll = false, isFetchOnMount = false, enableQueryString = true }) => {
	// 狀態管理（list、pagination、extend）
	const { listState: overviewData, setList, addItems, updateItem } = useListState();

	// 查詢參數與網址同步處理
	const { searchParams, setQuery } = useQuerySync({
		enable: enableQueryString,
		defaultParams: defaultSearchParams,
	});

	// API 呼叫封裝（自動判斷是否 append + 格式轉換）
	const { fetchOnce } = useOverviewFetch({
		fetcher: getOverviewApi,
		enableInfiniteScroll,
	});

	// 進入頁面時：若 URL query 有值就用 query，否則用 defaultSearchParams
	// 無論來源是哪個，都先依 defaultSearchParams schema 做 normalize（補預設/型別轉換）
	const normalizeFilters = filters => normalizeBySchema(filters || {}, defaultSearchParams);
	const currentSearchParams = enableQueryString ? searchParams : normalizeFilters(defaultSearchParams);
	const [localSearchParams, setLocalSearchParams] = useState(currentSearchParams);
	const [searchParamsVersion, setSearchParamsVersion] = useState(0);

	// 執行期（搜尋/換頁/排序）以 localSearchParams + latestFiltersRef 為主，
	// 避免依賴 replaceState 時 URL query 可能不觸發 re-render 的時序問題。
	// URL query 在本 Hook 的角色是「進頁初始化與同步」，不是每次互動的唯一即時來源。
	const latestFiltersRef = useRef(currentSearchParams);
	const prevQuerySearchParamsRef = useRef(searchParams);

	// 記錄最新一次查詢的序號，用來避免「舊請求回應較晚回來時覆蓋新結果／新搜尋條件」的競態問題（race condition）
	const latestRequestIdRef = useRef(0);

	useEffect(() => {
		// 只在 query source 真正變更時，才把 query 同步回本地搜尋條件
		// 避免 replaceState 不觸發 re-render 時，用舊 query 覆蓋剛送出的 local 條件
		if (enableQueryString && !isEqual(prevQuerySearchParamsRef.current, searchParams)) {
			if (!isEqual(localSearchParams, currentSearchParams)) {
				setLocalSearchParams(currentSearchParams);
			}
			if (!isEqual(latestFiltersRef.current, currentSearchParams)) {
				latestFiltersRef.current = currentSearchParams;
			}
			prevQuerySearchParamsRef.current = searchParams;
			return;
		}

		// 未啟用 query 同步時，仍跟隨 defaultSearchParams
		if (!enableQueryString) {
			if (!isEqual(localSearchParams, currentSearchParams)) {
				setLocalSearchParams(currentSearchParams);
			}
			if (!isEqual(latestFiltersRef.current, currentSearchParams)) {
				latestFiltersRef.current = currentSearchParams;
			}
		}
	}, [enableQueryString, searchParams, currentSearchParams, localSearchParams]);

	/**
	 * 根據 API 回傳結果，更新列表狀態（依照模式做 append 或覆蓋）
	 * @param {object} resp - 正規化後的 API 回傳資料
	 */
	const applyRespResult = useCallback(
		resp => {
			if (!resp || isEmpty(resp)) return;

			const { list, pagination, extend, mode } = resp;
			if (mode === "append") {
				addItems(list, pagination, extend);
			} else {
				setList(list, pagination, extend);
			}
		},
		// eslint-disable-next-line
		[addItems, setList],
	);

	/**
	 * 執行 API 查詢並更新狀態
	 * @param {object} filters - 查詢條件
	 * @param {boolean} [syncQuery=true] - 是否同步到網址（預設為 true）
	 */
	const getOverviewData = useCallback(
		async (filters, syncQuery = true) => {
			const normalizedFilters = normalizeFilters(filters);
			latestFiltersRef.current = normalizedFilters;

			// 每次呼叫都取號，等待期間若有更新的查詢送出，號碼會被蓋過去
			const requestId = ++latestRequestIdRef.current;

			try {
				const resp = await fetchOnce(normalizedFilters);

				// 這次的回應已經不是最新的查詢了（例如使用者在上一次查詢完成前又送出新的搜尋），
				// 直接捨棄，避免用「較晚回來的舊資料」蓋掉畫面上較新的查詢結果
				if (requestId !== latestRequestIdRef.current) {
					return;
				}

				applyRespResult(resp);

				if (!enableQueryString) {
					return;
				}

				// 更新 URL query string（視情況）
				if (syncQuery) {
					setQuery(normalizedFilters);
				}
			} catch (error) {
				console.error("取得總覽資料失敗：", error);
			} finally {
				// 同樣只在「仍是最新一次查詢」時才更新本地搜尋條件，
				// 避免舊請求的 finally 用過期的搜尋條件覆蓋掉使用者剛送出的新搜尋條件
				if (requestId === latestRequestIdRef.current) {
					setLocalSearchParams(normalizedFilters);
					setSearchParamsVersion(prev => prev + 1);
				}
			}
		},
		// eslint-disable-next-line
		[fetchOnce, applyRespResult, enableQueryString, setQuery, defaultSearchParams],
	);

	/**
	 * 換頁功能（只改 pageIndex）
	 */
	const updateOverviewPage = async pageIndex => {
		await getOverviewData({ ...(latestFiltersRef.current || {}), pageIndex });
	};

	/**
	 * 變更排序欄位（會回到第一頁）
	 */
	const updateOverviewSort = async sortBy => {
		await getOverviewData({ ...(latestFiltersRef.current || {}), sortBy, pageIndex: 1 });
	};

	/**
	 * 重置查詢條件為預設值
	 */
	const resetOverviewFilters = async () => await getOverviewData(defaultSearchParams);

	/**
	 * 更新列表中的單筆資料（根據 id）
	 */
	const updateOverviewItem = (id, data) => updateItem(id, data);

	/**
	 * 判斷首次進入畫面是否自動打 API
	 */
	const shouldAutoFetch = isEmpty(defaultSearchParams) ? isFetchOnMount : isFetchOnMount && (!enableQueryString || !isEmpty(searchParams));

	// 判斷是否有 mode=load 參數存在
	const autoLoad = (searchParams?.mode ?? null) === "load";

	// 進入畫面自動打 API（若符合條件）
	useEffectOnceWhen(
		() => {
			getOverviewData(currentSearchParams);
		},
		() => shouldAutoFetch || autoLoad, // 有 mode=load 參數也自動載入
	);

	return {
		searchParams: localSearchParams,
		searchParamsVersion,
		overviewData,
		getOverviewData,
		updateOverviewPage,
		updateOverviewSort,
		resetOverviewFilters,
		updateOverviewItem,
	};
};

export default useOverview;
